import {
  Button,
  cn,
  formatDate as formatUiDate,
  ResponsiveTable,
  useTranslation,
} from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import { useMoneyFormatters } from '../_money/useMoneyFormatters.js';
import type {
  AgreementStatus,
  ServiceAgreementListItem,
  ServiceAgreementsResponse,
} from './contractsMirror.js';
import type { ServiceAgreementsSearch } from './searchParams.js';

/**
 * The faithful 1:1 service-agreements list port (UI-SPEC Recurring cluster) on the locked Velocity
 * brand. Page wrapper + header (title, subtitle, the single "+ New Agreement" CTA) + a summary strip
 * (MRR, active count, due-this-month) + the All/Active/Paused/Cancelled status-filter pill row (each
 * pill a `Link` carrying its SERVER-computed count) + the agreements table — or the empty state.
 *
 * The coarse endpoint (22-12) owns all aggregation + the status counts; this view only renders the
 * returned payload. THE GUARDRAIL: rendering this list never generates an invoice (read-only GET);
 * the generate-invoice action lives on the detail screen.
 *
 * The "+ New Agreement" header CTA + the active status pill are the only orange-accent surfaces here
 * (UI-SPEC Color accent list). Every color is a locked `@hbs-us/ui` token (no hex, BRAND-06).
 */

const PILL_STATUSES: readonly AgreementStatus[] = ['active', 'paused', 'cancelled'];

const PILL_BASE =
  'inline-flex h-11 items-center gap-2 rounded-md border px-4 font-medium text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const PILL_ACTIVE = 'border-transparent bg-primary text-primary-foreground';
const PILL_INACTIVE = 'border-border bg-card text-foreground hover:bg-secondary';

function pillSearch(status: AgreementStatus | null): ServiceAgreementsSearch {
  return status ? { status } : {};
}

export interface ServiceAgreementsViewProps {
  data: ServiceAgreementsResponse;
  search: ServiceAgreementsSearch;
}

export function ServiceAgreementsView({ data, search }: ServiceAgreementsViewProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const fmt = useMoneyFormatters();

  const agreements = data.agreements ?? [];
  const statusCounts = data.statusCounts ?? {};
  const mrr = Number(data.summary?.mrr ?? 0);
  const activeCount = Number(data.summary?.activeCount ?? 0);
  const dueThisMonth = Number(data.summary?.dueThisMonth ?? 0);
  const totalCount = agreements.length;
  const active = search.status ?? null;
  const hasData = agreements.length > 0;

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {t('serviceAgreements:title')}
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-sm">
            {t('serviceAgreements:subtitle')}
          </p>
        </div>
        <Button asChild>
          <Link to="/service-agreements/new">{t('serviceAgreements:actions.newAgreement')}</Link>
        </Button>
      </header>

      <section
        aria-label={t('serviceAgreements:summary.regionLabel')}
        className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3"
      >
        <SummaryCard label={t('serviceAgreements:summary.mrr')} value={fmt.formatCurrency(mrr)} />
        <SummaryCard label={t('serviceAgreements:summary.active')} value={String(activeCount)} />
        <SummaryCard
          label={t('serviceAgreements:summary.dueThisMonth')}
          value={String(dueThisMonth)}
        />
      </section>

      <section
        aria-label={t('serviceAgreements:filter.regionLabel')}
        className="mb-4 rounded-md border border-border bg-card p-6"
      >
        <h2 className="mb-3 font-semibold text-foreground text-sm">
          {t('serviceAgreements:filter.heading')}
        </h2>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/service-agreements"
            search={pillSearch(null)}
            aria-current={active === null ? 'true' : undefined}
            className={cn(PILL_BASE, active === null ? PILL_ACTIVE : PILL_INACTIVE)}
          >
            {t('serviceAgreements:filter.all')}
            <span className="font-mono text-xs opacity-80">{totalCount}</span>
          </Link>
          {PILL_STATUSES.map((status) => {
            const isActive = active === status;
            return (
              <Link
                key={status}
                to="/service-agreements"
                search={pillSearch(status)}
                aria-current={isActive ? 'true' : undefined}
                className={cn(PILL_BASE, isActive ? PILL_ACTIVE : PILL_INACTIVE)}
              >
                {t(`serviceAgreements:status.${status}`)}
                <span className="font-mono text-xs opacity-80">{statusCounts[status] ?? 0}</span>
              </Link>
            );
          })}
        </div>
      </section>

      {hasData ? (
        <AgreementsTable agreements={agreements} fmt={fmt} locale={locale} />
      ) : (
        <div className="rounded-md border border-border bg-card p-16 text-center">
          <h2 className="font-display font-semibold text-foreground text-xl">
            {t('serviceAgreements:empty.heading')}
          </h2>
          <p className="mx-auto mt-2 max-w-sm text-muted-foreground text-sm">
            {t('serviceAgreements:empty.body')}
          </p>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-card p-6">
      <p className="text-muted-foreground text-sm">{label}</p>
      <p className="mt-1 font-display font-semibold text-2xl text-foreground">{value}</p>
    </div>
  );
}

function AgreementStatusBadge({ status }: { status: AgreementStatus }) {
  const { t } = useTranslation();
  // TEXT, never color alone (A11Y-01). Token-only backgrounds.
  const tone =
    status === 'active'
      ? 'bg-secondary text-secondary-foreground'
      : status === 'paused'
        ? 'border border-border bg-background text-muted-foreground'
        : 'bg-destructive/10 text-destructive';
  return (
    <span className={cn('inline-flex items-center rounded-md px-2 py-1 font-medium text-xs', tone)}>
      {t(`serviceAgreements:status.${status}`)}
    </span>
  );
}

function AgreementsTable({
  agreements,
  fmt,
  locale,
}: {
  agreements: ServiceAgreementListItem[];
  fmt: ReturnType<typeof useMoneyFormatters>;
  locale: string;
}) {
  const { t } = useTranslation();
  return (
    <ResponsiveTable className="rounded-md border border-border bg-card">
      <table className="w-full text-left text-sm">
        <thead className="border-border border-b text-muted-foreground">
          <tr>
            <th className="px-4 py-3 font-medium">{t('serviceAgreements:table.customer')}</th>
            <th className="px-4 py-3 font-medium">{t('serviceAgreements:table.service')}</th>
            <th className="px-4 py-3 font-medium">{t('serviceAgreements:table.amount')}</th>
            <th className="px-4 py-3 font-medium">{t('serviceAgreements:table.frequency')}</th>
            <th className="px-4 py-3 font-medium">{t('serviceAgreements:table.nextRun')}</th>
            <th className="px-4 py-3 font-medium">{t('serviceAgreements:table.status')}</th>
          </tr>
        </thead>
        <tbody>
          {agreements.map((a) => (
            <tr key={a.id} className="border-border border-b last:border-0 hover:bg-accent/40">
              <td className="px-4 py-3">
                <Link
                  to="/service-agreements/$id"
                  params={{ id: String(a.id) }}
                  className="font-medium text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {a.customerName ?? t('serviceAgreements:table.noCustomer')}
                </Link>
              </td>
              <td className="px-4 py-3 text-foreground">{a.serviceName}</td>
              <td className="px-4 py-3 font-mono text-foreground">
                {fmt.formatCurrency(a.amount)}
              </td>
              <td className="px-4 py-3 text-muted-foreground">
                {t(`serviceAgreements:frequency.${a.billingFrequency}`, {
                  defaultValue: a.billingFrequency,
                })}
              </td>
              <td className="px-4 py-3 font-mono text-muted-foreground">
                {formatUiDate(a.nextBillingDate, locale, { dateStyle: 'medium' })}
              </td>
              <td className="px-4 py-3">
                <AgreementStatusBadge status={a.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ResponsiveTable>
  );
}
